import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

type TelemetryUserContext = {
  id?: number | string;
  email?: string;
  username?: string;
};

type TelemetryPrimitive = string | number | boolean;

type TelemetryTransaction = {
  addLabels?: (labels: Record<string, TelemetryPrimitive>) => void;
  end?: () => void;
};

type TelemetryClient = {
  setUserContext?: (context: TelemetryUserContext) => void;
  captureError?: (error: unknown) => void;
  addLabels?: (labels: Record<string, TelemetryPrimitive>) => void;
  startTransaction?: (name: string, type?: string) => TelemetryTransaction;
};

type ElasticApmGlobal = {
  init?: (config: { serviceName: string; serverUrl: string }) => TelemetryClient;
  setUserContext?: (context: TelemetryUserContext) => void;
  captureError?: (error: unknown) => void;
  addLabels?: (labels: Record<string, TelemetryPrimitive>) => void;
  startTransaction?: (name: string, type?: string) => TelemetryTransaction;
};

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private client: TelemetryClient | null = null;

  initialize() {
    const elasticApm = this.getElasticApmGlobal();
    const serverUrl = environment.apmServiceURL;

    if (!elasticApm || !serverUrl) {
      return;
    }

    if (this.client) {
      return;
    }

    this.client = elasticApm.init
      ? elasticApm.init({
        serviceName: 'cabinet',
        serverUrl,
      })
      : elasticApm;

    this.setUserContext({ username: 'anonymous' });
  }

  setUserContext(context: TelemetryUserContext) {
    this.client?.setUserContext?.(context);
  }

  captureError(error: unknown) {
    this.client?.captureError?.(error);
  }

  captureEvent(name: string, labels: Record<string, TelemetryPrimitive> = {}) {
    if (!this.client) {
      return;
    }

    const transaction = this.client.startTransaction?.(name, 'custom');

    if (transaction) {
      transaction.addLabels?.(labels);
      transaction.end?.();
      return;
    }

    this.client.addLabels?.(labels);
  }

  private getElasticApmGlobal(): ElasticApmGlobal | null {
    const maybeWindow = window as unknown as { elasticApm?: ElasticApmGlobal };
    return maybeWindow.elasticApm || null;
  }
}
